// src/pages/UpdatePasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// --- Password Validation Function (Copied or Imported) ---
// It's good practice to move this to a shared utils file if used in multiple places
const validatePassword = (password) => {
    const errors = [];
    const minLength = 8;
    if (password.length < minLength) errors.push(`at least ${minLength} characters`);
    if (!/[a-z]/.test(password)) errors.push("a lowercase letter");
    if (!/[A-Z]/.test(password)) errors.push("an uppercase letter");
    if (!/\d/.test(password)) errors.push("a number");
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password)) errors.push("a special character (!@#$...)");
    return errors;
};
// ---

const UpdatePasswordPage = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordValidationErrors, setPasswordValidationErrors] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { user, updateUser, session } = useAuth(); // Get user and updateUser function
    const navigate = useNavigate();

    // --- Effect for Password Validation ---
     useEffect(() => {
         if (passwordTouched) {
             setPasswordValidationErrors(validatePassword(password));
         } else {
             setPasswordValidationErrors([]);
         }
     }, [password, passwordTouched]);

     // --- Effect to check if user should be here ---
     // Supabase client handles the session from the URL fragment automatically
     // The onAuthStateChange listener in AuthContext updates the session state
     useEffect(() => {
        // If after the initial context load there's no user/session,
        // the token might be invalid/expired, or the user isn't logged in via recovery link.
        // Redirecting immediately might be too aggressive if context is still loading briefly.
        // Relying on updateUser failure might be sufficient.
        // Optionally add a check after a short delay if needed.
        // console.log("Update password page session:", session);
     }, [session]);


    // --- Form Submission Handler ---
    const handleUpdatePasswordSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');

        // Validations
        const complexityErrors = validatePassword(password);
        if (complexityErrors.length > 0) {
            setError(`Password is too weak. It must contain: ${complexityErrors.join(', ')}.`);
            setPasswordTouched(true);
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            // Call the updateUser function from context
            const { error: updateError } = await updateUser({ password: password }); // Pass object with password
            if (updateError) throw updateError;

            setMessage('Password updated successfully! Redirecting to login...');
            setPassword(''); // Clear fields
            setConfirmPassword('');
            setPasswordTouched(false);
            // Redirect to login page after a short delay
            setTimeout(() => {
                navigate('/auth'); // Or navigate('/main-menu') if you want to log them in
            }, 2000);

        } catch (err) {
            console.error("Update Password error:", err);
            setError(err.message || 'Failed to update password.');
        } finally {
            setLoading(false);
        }
    };

    // --- Password Requirements Display Component (Copied or Imported) ---
    const PasswordRequirements = () => {
        if (!passwordTouched) return null; // Only show after interaction
        const requirements = [ /* ... (same requirements array as in AuthPage) ... */ ];
        return (
            <div className="text-xs mt-2 pl-1 space-y-1">
                <p className="font-medium mb-1 text-base-content/70">Password must contain:</p>
                <ul className="list-inside">
                    {requirements.map(req => (
                        <li key={req.label} className={`transition-colors duration-200 ${req.met ? 'text-success line-through opacity-70' : 'text-error'}`}>
                            {req.label}
                        </li>
                     ))}
                 </ul>
            </div>
        );
    };

    // --- Render Component ---
    return (
        <div className="min-h-screen flex items-center justify-center bg-base-200 py-12 px-4 sm:px-6 lg:px-8">
            <div className="card w-full max-w-md bg-base-100 shadow-xl">
                <div className="card-body">
                    <h2 className="card-title justify-center text-2xl mb-6 font-bold text-base-content">
                        Set Your New Password
                    </h2>

                    {/* Error Message */}
                    {error && (
                        <div role="alert" className="alert alert-error shadow-lg mb-4">
                             <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                             <span>{error}</span>
                         </div>
                    )}
                    {/* Success Message */}
                    {message && (
                         <div role="alert" className="alert alert-success shadow-lg mb-4">
                             <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                             <span>{message}</span>
                         </div>
                    )}

                    {/* Don't show form if success message is displayed */}
                    {!message && (
                        <form onSubmit={handleUpdatePasswordSubmit} className="space-y-4">
                            {/* New Password Input */}
                            <div className="form-control">
                                <label className="label"><span className="label-text text-base-content/80">New Password</span></label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••" autoComplete="new-password"
                                        className="input input-bordered w-full pr-10" value={password}
                                        onBlur={() => setPasswordTouched(true)}
                                        onChange={(e) => setPassword(e.target.value)} required disabled={loading}
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-base-content/50 hover:text-base-content/70 focus:outline-none" aria-label={showPassword ? "Hide password" : "Show password"}>
                                        {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Password Requirements Display */}
                            <PasswordRequirements />

                            {/* Confirm New Password Input */}
                            <div className="form-control">
                                <label className="label"><span className="label-text text-base-content/80">Confirm New Password</span></label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="••••••••" autoComplete="new-password"
                                        className="input input-bordered w-full pr-10" value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)} required disabled={loading}
                                    />
                                     <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-base-content/50 hover:text-base-content/70 focus:outline-none" aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}>
                                         {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                     </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="form-control pt-2">
                                <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                                    {loading ? <span className="loading loading-spinner loading-sm"></span> : 'Update Password'}
                                </button>
                            </div>
                        </form>
                     )} {/* End conditional form rendering */}

                </div> {/* End card-body */}
            </div> {/* End card */}
        </div> // End page container
    );
};

export default UpdatePasswordPage;